import { useState, useEffect, useMemo } from "react";
import { useCartMutations } from "./useCartMutations";
import {
  CountriesAndLanguages,
  countryStates,
} from "@/networking/addresses.api";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { useAddressFields } from "@/hooks/useAddressFields";
import { State, US_STATES, getRegionConfig } from "@/lib/country-config";
import { isApiError } from "@/lib/api-helpers";

const EMPTY_COUNTRIES: CountriesAndLanguages["countries"] = [];
const EMPTY_STATES: State[] = [];

export default function useCartCountry(
  cartToken: string,
  paymentMethodsIncludes: string | null,
  initialCountry?: string,
  countries: CountriesAndLanguages["countries"] = EMPTY_COUNTRIES,
) {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<string>(
    initialCountry || "US",
  );
  const { updateCountry } = useCartMutations(cartToken, paymentMethodsIncludes);

  const stateOptions = useMemo(() => {
    const translatedStates = countryStates(countries, selectedCountry);
    return (
      translatedStates ?? (selectedCountry === "US" ? US_STATES : EMPTY_STATES)
    );
  }, [countries, selectedCountry]);

  useEffect(() => {
    if (initialCountry) {
      setSelectedCountry(initialCountry);
    }
  }, [initialCountry]);

  const handleCountryChange = async (
    selectedCountryCode: string,
    updateCartCountry: boolean = true,
  ) => {
    const previousCountry = selectedCountry;
    setSelectedCountry(selectedCountryCode);

    if (selectedCountryCode) {
      let countryUpdateSucceeded = false;
      try {
        if (updateCartCountry) {
          toast.loading("Updating cart country...", {
            id: selectedCountryCode,
          });
          await updateCountry.mutateAsync({
            country_code: selectedCountryCode,
          });
          toast.success(t("cart_country_updated"), { id: selectedCountryCode });
          countryUpdateSucceeded = true;
        }
      } catch (error) {
        console.error("Country update error:", error);
        if (updateCartCountry && !countryUpdateSucceeded) {
          setSelectedCountry(previousCountry);
        }
        const message = isApiError(error)
          ? error.message
          : t("failed_to_update_cart_country");
        toast.error(message, { id: selectedCountryCode });
      }
    }
  };

  const config = getRegionConfig(selectedCountry);
  const { addressFields, isLoadingAddressFields } =
    useAddressFields(selectedCountry);

  return {
    handleCountryChange,
    stateOptions,
    config,
    selectedCountry,
    isUpdatingCountry: updateCountry.isPending,
    addressFields,
    isLoadingAddressFields,
  };
}
